import AnimatedScore from '@/components/AnimatedScore/AnimatedScore'
import Team from '@/components/Team/Team'
import { getSportConfig, SPORT_ID } from '@/config/sports'
import { useTheme } from '@/contexts/ThemeContext'
import { useResolution } from '@/hooks/useResolution'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'

import CardDivs from './CardDivs'
import { resolveCardColors, totalCardCount } from './cards'
import { deriveElapsedSeconds, formatClock, resolveBreakLabel, resolvePeriodLabel, useTickingNow } from './clock'
import './GoalSportBar.css'

import type { JsonMatchData, SoccerScore, GoalSportEventState } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface GoalSportBarProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
}

const MULTIPLIERS = {
  scoreFontSize: 1.3,
  periodFontSize: 0.5,
  clockFontSize: 0.65,
  cardWidth: 0.18,
  cardHeight: 0.46,
}

const GoalSportBar: FC<GoalSportBarProps> = ({ jsonData, isVisible = true }) => {
  const theme = useTheme()
  const resolution = useResolution()
  const animationRef = useTemplateAnimation(isVisible, { animationType: 'slideUp', duration: 0.5 })

  // Sport config drives labels.
  const sportId = (jsonData?.sportId as SPORT_ID | undefined) ?? SPORT_ID.SOCCER
  const sportConfig = getSportConfig(sportId)
  const periodLabels = sportConfig?.periodLabels
  const breakLabels = sportConfig?.breakLabels
  const endLabel = sportConfig?.endLabel ?? 'Full Time'

  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  const homeScore = homeCompetitor?.score as SoccerScore | undefined
  const awayScore = awayCompetitor?.score as SoccerScore | undefined

  const homeGoals = homeScore?.goals ?? 0
  const awayGoals = awayScore?.goals ?? 0
  const homeCards = homeScore?.cards ?? {}
  const awayCards = awayScore?.cards ?? {}

  const cardColors = resolveCardColors(sportId)
  const homeHasCards = totalCardCount(homeCards, cardColors) > 0
  const awayHasCards = totalCardCount(awayCards, cardColors) > 0

  const eventState = jsonData?.eventState as GoalSportEventState | undefined
  const phase = eventState?.phase ?? 'PRE'
  const period = Math.max(1, eventState?.period ?? 1)

  const isRunning = phase === 'RUNNING'
  const now = useTickingNow(isRunning)
  const elapsedSeconds = deriveElapsedSeconds(eventState, now)
  const clockText = formatClock(elapsedSeconds)

  let periodLabel: string
  if (phase === 'PRE') {
    periodLabel = resolvePeriodLabel(1, periodLabels)
  } else if (phase === 'BREAK') {
    periodLabel = resolveBreakLabel(period, breakLabels)
  } else if (phase === 'END') {
    periodLabel = endLabel
  } else {
    periodLabel = resolvePeriodLabel(period, periodLabels)
  }

  const showClock = phase !== 'PRE'

  const primaryBg = hexToRgba(theme.colors.primary, 0.99)

  // Trapezoid clip for branding row (matches Baseball bar visual language).
  const trapP = `calc(${theme.baseHeight} * 0.6)`
  const brandingClipPath = `polygon(calc(35% + ${trapP}) 0, calc(65% - ${trapP}) 0, 65% 100%, 35% 100%)`

  const cardStyle = {
    width: resolution.height,
    '--bar-row-height': `calc(${theme.baseHeight} * 1.667)`,
    '--primary-bg': primaryBg,
  } as React.CSSProperties

  return (
    <div ref={animationRef} className="goal-sport-bar-content-wrapper">
      <div className="goal-sport-bar-card goal-sport-bar-grid" style={cardStyle} data-scorebar-panel>
        {/* Row 1: Branding */}
        <div className="goal-sport-bar-branding-row" style={{ clipPath: brandingClipPath, height: theme.baseHeight }}>
          <div />
          <div className="goal-sport-bar-branding-center">
            <img
              src="/assets/frogbox-logo-mono.png"
              alt="FrogBox"
              style={{ height: `calc(${theme.baseHeight} * 0.5)` }}
            />
          </div>
          <div />
        </div>

        {/* Row 2: Bar */}
        <div className="goal-sport-bar-row">
          <div className="goal-sport-bar-side">
            <Team data={homeCompetitor} isHome style={{ paddingRight: 0 }} />
            <div
              className="goal-sport-bar-score-stack"
              style={{
                marginLeft: 'auto',
                marginRight: `calc(${theme.baseHeight} * 1)`,
                gap: `calc(${theme.baseHeight} * 0.2)`,
              }}
            >
              <AnimatedScore
                value={homeGoals}
                className="goal-sport-bar-score"
                style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
              />
              {homeHasCards && (
                <CardDivs
                  cards={homeCards}
                  cardColors={cardColors}
                  baseHeight={theme.baseHeight}
                  widthMultiplier={MULTIPLIERS.cardWidth}
                  heightMultiplier={MULTIPLIERS.cardHeight}
                  className="goal-sport-bar-cards"
                  ariaPrefix="Home "
                />
              )}
            </div>
          </div>

          <div
            className="goal-sport-bar-center-group"
            style={{
              gap: `calc(${theme.baseHeight} * 0.18)`,
              padding: `calc(${theme.baseHeight} * 0.1) calc(${theme.baseHeight} * 0.5) calc(${theme.baseHeight} * 0.35)`,
            }}
          >
            <span
              className="goal-sport-bar-period-label"
              style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.periodFontSize})` }}
            >
              {periodLabel}
            </span>
            {showClock && (
              <span
                className="goal-sport-bar-clock"
                style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.clockFontSize})` }}
              >
                {clockText}
              </span>
            )}
          </div>

          <div className="goal-sport-bar-side">
            <div
              className="goal-sport-bar-score-stack"
              style={{
                marginLeft: `calc(${theme.baseHeight} * 1)`,
                gap: `calc(${theme.baseHeight} * 0.2)`,
              }}
            >
              <AnimatedScore
                value={awayGoals}
                className="goal-sport-bar-score"
                style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
              />
              {awayHasCards && (
                <CardDivs
                  cards={awayCards}
                  cardColors={cardColors}
                  baseHeight={theme.baseHeight}
                  widthMultiplier={MULTIPLIERS.cardWidth}
                  heightMultiplier={MULTIPLIERS.cardHeight}
                  className="goal-sport-bar-cards"
                  ariaPrefix="Away "
                />
              )}
            </div>
            <Team data={awayCompetitor} style={{ paddingLeft: 0, marginLeft: 'auto' }} align="right" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default GoalSportBar
